import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { BottomTabBar } from '@/components/layout/BottomTabBar'
import { apiUrl } from '@/lib/api'

interface GuardianRequest {
  id: string
  lineDisplayName: string
  linePictureUrl: string | null
  studentNameText: string
  guardianName: string
  relationship: string
  phone: string
  status: string
  createdAt: string
}

interface Student {
  id: string
  studentNumber: number
  prefix: string
  fullName: string
  nickname: string | null
}

export function KYCQueuePage() {
  const navigate = useNavigate()
  const [requests, setRequests] = useState<GuardianRequest[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [approveModal, setApproveModal] = useState<GuardianRequest | null>(null)
  const [students, setStudents] = useState<Student[]>([])
  const [studentSearch, setStudentSearch] = useState('')
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  useEffect(() => {
    fetchRequests()
  }, [])

  async function fetchRequests() {
    setIsLoading(true)
    try {
      const res = await fetch(apiUrl('/api/guardian-requests?status=pending'), { credentials: 'include' })
      const data = await res.json()
      if (data.success) setRequests(data.data)
    } catch {}
    setIsLoading(false)
  }

  async function openApproveModal(request: GuardianRequest) {
    setApproveModal(request)
    setSelectedStudentId(null)
    setStudentSearch('')
    // Fetch students for search
    const res = await fetch(apiUrl('/api/students'), { credentials: 'include' })
    const data = await res.json()
    if (data.success) setStudents(data.data.students)
  }

  async function handleApprove() {
    if (!approveModal || !selectedStudentId) return
    const res = await fetch(apiUrl(`/api/guardian-requests/${approveModal.id}/approve`), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ studentId: selectedStudentId }),
    })
    const data = await res.json()
    if (data.success) {
      setRequests((prev) => prev.filter((r) => r.id !== approveModal.id))
      showToast('อนุมัติเรียบร้อย')
    } else {
      showToast(data.error?.messageTh || 'เกิดข้อผิดพลาด')
    }
    setApproveModal(null)
  }

  async function handleReject(id: string) {
    const res = await fetch(apiUrl(`/api/guardian-requests/${id}/reject`), {
      method: 'POST',
      credentials: 'include',
    })
    const data = await res.json()
    if (data.success) {
      setRequests((prev) => prev.filter((r) => r.id !== id))
      showToast('ปฏิเสธเรียบร้อย')
    }
  }

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 2000)
  }

  function timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 60) return `${mins} นาทีที่แล้ว`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours} ชม. ที่แล้ว`
    return `${Math.floor(hours / 24)} วันที่แล้ว`
  }

  const filteredStudents = studentSearch.trim()
    ? students.filter(
        (s) =>
          s.fullName.includes(studentSearch) ||
          (s.nickname && s.nickname.includes(studentSearch)) ||
          String(s.studentNumber).includes(studentSearch),
      )
    : students

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="text-gray-600">
            ⟨
          </button>
          <h1 className="text-lg font-bold text-gray-900">
            ตรวจสอบผู้ปกครอง ({requests.length})
          </h1>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-4">
        {isLoading ? (
          <div className="text-center text-sm text-gray-400">กำลังโหลด...</div>
        ) : requests.length === 0 ? (
          <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-400 shadow-sm">
            ไม่มีคำขอรอตรวจสอบ
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-500">รอการตรวจสอบ {requests.length} คำขอ</p>
            {requests.map((req) => (
              <div key={req.id} className="rounded-lg bg-white p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                    {req.linePictureUrl ? (
                      <img
                        src={req.linePictureUrl}
                        alt=""
                        className="h-12 w-12 rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-lg">👤</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{req.guardianName}</p>
                    <p className="text-xs text-gray-400">LINE: {req.lineDisplayName}</p>
                  </div>
                </div>

                <div className="mt-3 space-y-1 text-sm text-gray-600">
                  <p>
                    แจ้งว่าเป็นผู้ปกครองของ:{' '}
                    <span className="font-medium">"{req.studentNameText}"</span>
                  </p>
                  <p>ความสัมพันธ์: {req.relationship}</p>
                  <p>เบอร์: {req.phone}</p>
                  <p className="text-xs text-gray-400">ส่งคำขอ: {timeAgo(req.createdAt)}</p>
                </div>

                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => handleReject(req.id)}
                    className="flex-1 rounded-lg border border-gray-200 py-2.5 text-sm font-medium text-gray-600"
                  >
                    ✗ ปฏิเสธ
                  </button>
                  <button
                    onClick={() => openApproveModal(req)}
                    className="flex-1 rounded-lg bg-primary-600 py-2.5 text-sm font-semibold text-white"
                  >
                    ✓ อนุมัติ
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Approve Modal — Student Search */}
      {approveModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
          <div className="w-full max-w-sm rounded-t-xl bg-white p-5 shadow-xl sm:rounded-xl">
            <h3 className="text-base font-bold text-gray-900">เชื่อมกับนักเรียนคนไหน?</h3>
            <p className="mt-1 text-xs text-gray-400">
              คำขอจาก: {approveModal.guardianName} → "{approveModal.studentNameText}"
            </p>

            <input
              type="text"
              placeholder="🔍 ค้นหาชื่อนักเรียน"
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
              className="mt-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
            />

            <div className="mt-2 max-h-48 overflow-y-auto">
              {filteredStudents.map((s) => (
                <label
                  key={s.id}
                  className={`flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm ${
                    selectedStudentId === s.id ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="student"
                    checked={selectedStudentId === s.id}
                    onChange={() => setSelectedStudentId(s.id)}
                    className="sr-only"
                  />
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded-full border-2 text-xs ${
                      selectedStudentId === s.id
                        ? 'border-primary-500 bg-primary-500 text-white'
                        : 'border-gray-300'
                    }`}
                  >
                    {selectedStudentId === s.id && '✓'}
                  </span>
                  <span>
                    {s.studentNumber} · {s.prefix}{s.fullName}
                    {s.nickname && <span className="text-gray-400"> ({s.nickname})</span>}
                  </span>
                </label>
              ))}
            </div>

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => setApproveModal(null)}
                className="flex-1 rounded-lg border border-gray-200 py-2.5 text-sm font-medium text-gray-600"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleApprove}
                disabled={!selectedStudentId}
                className="flex-1 rounded-lg bg-primary-600 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                ยืนยันอนุมัติ
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed left-1/2 top-4 z-50 -translate-x-1/2 rounded-lg bg-gray-800 px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <BottomTabBar active="more" />
    </div>
  )
}
